import { UserCircleIcon } from "@phosphor-icons/react/dist/csr/UserCircle";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import { cn } from "@volca/ui/lib/utils";
import { useAssignDocument, useGetApAssignees } from "@/accounts-payable/query";
import {
  AP_UNASSIGNED,
  type ApAssignee,
  type ApPurchaseDocument,
} from "@/accounts-payable/types";
import useAuth from "@/core/auth/use-auth";

export const AssigneeSelect = ({
  document,
  businessId,
  compact = false,
}: {
  document: Pick<ApPurchaseDocument, "id" | "document_type" | "assignee">;
  businessId: string | null;
  compact?: boolean;
}) => {
  const { user } = useAuth();
  const { data: people, isPending, isError } = useGetApAssignees(businessId);
  const assign = useAssignDocument(businessId, document.document_type);
  const current = document.assignee ?? null;
  const me = people?.find((person) => person.id === user?.id);
  const options: ApAssignee[] = [
    ...(me ? [me] : []),
    ...(people ?? []).filter((person) => person !== me),
    ...(current && !people?.some((person) => person.id === current.id)
      ? [current]
      : []),
  ];

  return (
    <Select
      value={current?.id ?? AP_UNASSIGNED}
      items={[
        { value: AP_UNASSIGNED, label: "Unassigned" },
        ...options.map((person) => ({
          value: person.id,
          label: person.id === user?.id ? `${person.name} (you)` : person.name,
        })),
      ]}
      disabled={assign.isPending}
      onValueChange={(id) =>
        assign.mutate({
          documentId: document.id,
          assignee: options.find((person) => person.id === id) ?? null,
        })
      }
    >
      <SelectTrigger
        aria-label="Assigned to"
        className={cn("gap-1.5 text-sm shadow-none", {
          "h-9 w-auto max-w-36 sm:h-8 sm:max-w-56": compact,
          "h-9": !compact,
        })}
      >
        <UserCircleIcon aria-hidden className="h-4 w-4 flex-none text-subtle" />
        <span
          className={cn("min-w-0 flex-1 truncate text-left", {
            "text-subtle": !current,
          })}
        >
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent align="end">
        <SelectGroup>
          <SelectItem value={AP_UNASSIGNED}>Unassigned</SelectItem>
          {options.map((person) => (
            <SelectItem key={person.id} value={person.id}>
              {person.id === user?.id ? `${person.name} (you)` : person.name}
            </SelectItem>
          ))}
          {isPending && (
            <SelectItem value="loading" disabled>
              Loading people…
            </SelectItem>
          )}
          {isError && (
            <SelectItem value="error" disabled>
              Couldn't load people. Please try again.
            </SelectItem>
          )}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};
